'use client';

import { formatDistanceToNow } from 'date-fns';
import { MoreHorizontalIcon, Pin, Trash2 } from 'lucide-react';

import { EmployeeAvatar } from '@/components/molecules/employee-avatar';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface NoteProps {
  note: ApiSchemas['NoteRead'];
  onPin: (id: string, pinned: boolean) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  isPinning: boolean;
}

export default function NoteComponent({ note, onPin, onDelete, isDeleting, isPinning }: NoteProps) {
  const isPinned = note.pinned_at !== null;

  const handlePin = () => {
    onPin(note.id, !isPinned);
  };

  const handleDelete = () => {
    onDelete?.(note.id);
  };

  return (
    <div className='flex w-full items-start gap-4 py-3'>
      <EmployeeAvatar employeeId={note.created_by_id} />
      <div className='flex flex-col gap-1'>
        <div className='text-sm break-all whitespace-pre-wrap'>{note.content}</div>
        <div className='text-xs text-muted-foreground'>{formatDistanceToNow(note.created_at, { addSuffix: true })}</div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='sm' className='ml-auto h-8 w-8 p-0' aria-label='Open menu'>
            <MoreHorizontalIcon className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-40'>
          <DropdownMenuItem onClick={handlePin} className='cursor-pointer'>
            <Pin className='mr-2 h-4 w-4' />
            {isPinned ? 'Unpin' : 'Pin'} note
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleDelete}
            className='cursor-pointer text-red-600 focus:text-red-600'
            disabled={isDeleting || isPinning}
          >
            <Trash2 className='mr-2 h-4 w-4' />
            Delete note
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
